"use client"

import type { ColumnDef } from "@tanstack/react-table"
import { observer } from "mobx-react-lite"
import { DataTable } from "@/components/DataTable"
import { QuickInsightCard } from "@/components/QuickInsightCard"
import { cn } from "@/lib/utils"
import type { InsightDashboardStoreType, ThemeConfigType } from "@/stores/DeveloperInsightsStore"

interface InsightDashboardProps {
  store: InsightDashboardStoreType
  theme?: ThemeConfigType
  className?: string
  tableTitle?: string
  enableSearch?: boolean
  searchPlaceholder?: string
  enableCsvExport?: boolean
  csvFilename?: string
  dataTableTranslations?: {
    filtersButton?: string
    filterBy?: string
    noResults?: string
    rowsPerPage?: string
    page?: string
    of?: string
    totalRows?: string
  }
  /** When true, hides the data table from the component. Useful when you want to render it separately. */
  hideTable?: boolean
}

export const InsightDashboard = observer(
  ({
    store,
    theme,
    className,
    tableTitle,
    enableSearch = true,
    searchPlaceholder = "Search...",
    enableCsvExport = true,
    csvFilename = "insight-data.csv",
    dataTableTranslations,
    hideTable = false,
  }: InsightDashboardProps) => {
    const handleInsightClick = (insightId: string) => {
      store.selectInsight(insightId)
    }

    const selectedInsightId = store.selectedInsight?.id

    return (
      <div className={cn("space-y-6", className)}>
        {/* Quick Insight Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {store.insights.map((insightWithData) => {
            const insight = insightWithData.insight
            return (
              <QuickInsightCard
                key={insight.id}
                subtitle={insight.subtitle}
                date={insight.date}
                title={insight.title}
                value={insight.value}
                secondaryText={insight.secondaryText}
                chartData={insight.chartData}
                theme={theme}
                onClick={() => handleInsightClick(insightWithData.id)}
                isSelected={selectedInsightId === insightWithData.id}
              />
            )
          })}
        </div>

        {/* Data Table (conditionally rendered) */}
        {!hideTable &&
          store.selectedTableData.length > 0 &&
          store.selectedTableColumns.length > 0 && (
            <DataTable
              title={tableTitle}
              columns={store.selectedTableColumns as ColumnDef<Record<string, unknown>>[]}
              data={store.selectedTableData}
              theme={theme}
              enableSearch={enableSearch}
              searchPlaceholder={searchPlaceholder}
              enableCsvExport={enableCsvExport}
              csvFilename={csvFilename}
              translations={dataTableTranslations}
            />
          )}
      </div>
    )
  },
)

InsightDashboard.displayName = "InsightDashboard"
